import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ChevronDown, ChevronUp, Send, PenLine, Copy, UserCheck, GitMerge, AlertCircle } from "lucide-react";
import MergeCompareModal from "@/components/MergeCompareModal";

const Section = ({ title, count, children }) => (
  <div className="mb-4">
    <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2">{title} {count > 0 && <span className="ml-1 text-slate-500">({count})</span>}</div>
    {children}
  </div>
);

export function MergeModalLegacy({ group, onClose, onDone }) {
  const [keepId, setKeepId] = useState(group.schede[0]?.id);
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);
  const keep = group.schede.find((s) => s.id === keepId);
  const tipo = keep?.tipo;
  const drops = group.schede.filter((s) => s.id !== keepId && s.tipo === tipo);
  const altri = group.schede.filter((s) => s.id !== keepId && s.tipo !== tipo);

  const vediAnteprima = async () => {
    setBusy(true);
    try {
      const totals = { appuntamenti: 0, documenti: 0, comunicazioni: 0, storico: 0 };
      for (const dr of drops) {
        const r = await api.get(`/clienti/unisci/anteprima?tipo=${tipo}&keep_id=${keepId}&drop_id=${dr.id}`);
        Object.keys(totals).forEach((k) => (totals[k] += r.data.sposta[k] || 0));
      }
      setAnteprima(totals);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(false); }
  };

  const conferma = async () => {
    setBusy(true);
    try {
      for (const dr of drops) await api.post("/clienti/unisci", { tipo, keep_id: keepId, drop_id: dr.id });
      toast.success("Schede unite: nessun dato perso");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore unione"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="merge-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-[var(--border)] w-full max-w-md p-5 shadow-xl">
        <div className="flex items-center gap-2 mb-1 font-display font-extrabold text-lg"><GitMerge size={18} className="text-[#FC82ED]" /> Unisci schede doppie</div>
        <p className="text-xs text-[var(--muted)] mb-3">Telefono: {group.telefono}. Scegli la scheda da <b>mantenere</b>; le altre dello stesso tipo verranno unite e archiviate. Nessun dato andrà perso.</p>
        <div className="space-y-1.5 mb-3">
          {group.schede.map((s) => (
            <label key={s.id} data-testid={`merge-keep-${s.id}`} className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm cursor-pointer ${keepId === s.id ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)]"}`}>
              <input type="radio" name="keep" checked={keepId === s.id} onChange={() => { setKeepId(s.id); setAnteprima(null); }} />
              <span className="font-medium">{s.nome}</span>
              <span className="ml-auto text-[10px] uppercase font-bold text-[var(--muted)]">{s.tipo}</span>
            </label>
          ))}
        </div>
        {altri.length > 0 && <div data-testid="merge-skip-note" className="mb-3 text-[11px] text-amber-600 flex items-center gap-1"><AlertCircle size={12} /> {altri.length} scheda/e di tipo diverso non verranno unite (restano separate).</div>}
        {anteprima && (
          <div data-testid="merge-anteprima" className="mb-3 p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-xs">
            <div className="flex items-center gap-1.5 font-semibold mb-1.5 text-slate-700"><AlertCircle size={13} /> Passeranno alla scheda che resta:</div>
            <div className="grid grid-cols-2 gap-1 text-slate-600">
              <span>Appuntamenti: <b>{anteprima.appuntamenti}</b></span>
              <span>Documenti: <b>{anteprima.documenti}</b></span>
              <span>Comunicazioni: <b>{anteprima.comunicazioni}</b></span>
              <span>Storico: <b>{anteprima.storico}</b></span>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
          {!anteprima
            ? <button data-testid="merge-anteprima-btn" onClick={vediAnteprima} disabled={busy || !drops.length} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-50">{busy ? "..." : "Anteprima"}</button>
            : <button data-testid="merge-confirm-btn" onClick={conferma} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold disabled:opacity-50">{busy ? "Unisco..." : "Conferma unione"}</button>}
        </div>
      </div>
    </div>
  );
}

export default function GuardianaPanel({ all }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [mergeGroup, setMergeGroup] = useState(null);

  const load = () => api.get(`/dashboard/guardiana?all=${all ? 1 : 0}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open, all]);

  const inviaModulo = async (s) => { try { await api.post(`/clienti/${s.tipo}/${s.id}/invia-modulo-privacy`); toast.success("Modulo privacy inviato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const registraConsenso = async (s) => { try { await api.post(`/clienti/${s.tipo}/${s.id}/registra-consenso`); toast.success("Consenso registrato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const taskAgente = async (s) => { try { await api.post(`/clienti/${s.tipo}/${s.id}/task-completa-scheda`); toast.success("Task inviato all'agente"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  const tot = d ? (d.senza_consenso.length + d.doppioni.length + d.incomplete.length) : 0;

  return (
    <div className="card-immo p-4 mb-6" data-testid="guardiana-panel">
      <button data-testid="guardiana-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left">
        <ShieldCheck size={16} className="text-[#FC82ED]" />
        <span className="font-display font-extrabold">Guardiana dei dati</span>
        {tot > 0 && <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">{tot}</span>}
        <span className="ml-auto text-[var(--muted)]">{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
      </button>

      {open && (
        <div className="mt-4">
          {!d ? <div className="skeleton h-20 rounded-xl" /> : (
            <>
              <Section title="Consensi mancanti" count={d.senza_consenso.length}>
                {d.senza_consenso.length === 0 ? <p className="text-sm text-[var(--muted)]">Tutto in regola. 👍</p> :
                  <div className="space-y-2">
                    {d.senza_consenso.map((s) => (
                      <div key={s.tipo + s.id} data-testid={`consenso-${s.id}`} className="flex items-center justify-between gap-2 flex-wrap p-3 rounded-xl border border-[var(--border)]">
                        <div><div className="text-sm font-semibold">{s.nome}</div><div className="text-xs text-[var(--muted)]">{s.telefono}{s.modulo_inviato ? " · modulo inviato" : ""}</div></div>
                        <div className="flex gap-1.5">
                          <button data-testid={`consenso-invia-${s.id}`} onClick={() => inviaModulo(s)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium"><Send size={13} /> Invia modulo privacy</button>
                          <button data-testid={`consenso-registra-${s.id}`} onClick={() => registraConsenso(s)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold"><PenLine size={13} /> Registra consenso</button>
                        </div>
                      </div>
                    ))}
                  </div>}
              </Section>

              <Section title="Contatti doppi" count={d.doppioni.length}>
                {d.doppioni.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun doppione. 👍</p> :
                  <div className="space-y-2">
                    {d.doppioni.map((g) => (
                      <div key={g.telefono} data-testid={`doppione-${g.telefono}`} className="flex items-center justify-between gap-2 flex-wrap p-3 rounded-xl border border-amber-200 bg-amber-50/40">
                        <div><div className="text-sm font-semibold flex items-center gap-1.5"><Copy size={13} /> {g.telefono}</div><div className="text-xs text-[var(--muted)]">{g.schede.map((s) => s.nome).join(" · ")}</div></div>
                        <button data-testid={`doppione-unisci-${g.telefono}`} onClick={() => setMergeGroup(g)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold"><GitMerge size={13} /> Unisci</button>
                      </div>
                    ))}
                  </div>}
              </Section>

              <Section title="Schede incomplete" count={d.incomplete.length}>
                {d.incomplete.length === 0 ? <p className="text-sm text-[var(--muted)]">Tutte complete. 👍</p> :
                  <div className="space-y-2">
                    {d.incomplete.map((s) => (
                      <div key={s.tipo + s.id} data-testid={`incompleta-${s.id}`} className="flex items-center justify-between gap-2 flex-wrap p-3 rounded-xl border border-[var(--border)]">
                        <div><div className="text-sm font-semibold">{s.nome}</div><div className="text-xs text-red-500">Manca: {s.mancano.join(", ")}</div></div>
                        <button data-testid={`incompleta-task-${s.id}`} onClick={() => taskAgente(s)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium"><UserCheck size={13} /> Task all'agente</button>
                      </div>
                    ))}
                  </div>}
              </Section>
            </>
          )}
        </div>
      )}

      {mergeGroup && <MergeCompareModal group={mergeGroup} onClose={() => setMergeGroup(null)} onDone={() => { setMergeGroup(null); load(); }} />}
    </div>
  );
}
